import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import ReportTable from "../../common/ReportTable/ReportTable";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import BadgeMultiSelect from "../../common/BadgeMultiSelect/BadgeMultiSelect";
import ConfirmModal from "../../common/ConfirmModal";
import CtmsWorkspaceModal from "../../common/ctms/CtmsWorkspaceModal";
import TextEditor from "../../common/TextEditor/TextEditor";
import { useUserData } from "../../../hooks/useUserData";
import { useLoader } from "../../../context/LoaderContext";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import { stripHtml } from "../../../utils/avatarUtils";
import {
  archiveCampaignTemplate,
  unarchiveCampaignTemplate,
  createCampaignTemplate,
  fetchAllCampaignTemplates,
  fetchCampaignTemplateById,
} from "./participantService";
import { useParticipantAccess } from "./participantAccess";
import {
  CAMPAIGN_STATUS_FILTER_OPTIONS,
  CAMPAIGN_TEMPLATE_FIELD_OPTIONS,
  DEFAULT_CAMPAIGN_TEMPLATE_COLUMNS,
  getCampaignStatusPillStyle,
  getTemplateRowId,
  matchesCampaignStatusFilter,
  matchTextColumnSearch,
} from "./participantReportUtils";
import "./participantmanager.scss";

const EMPTY_FORM = {
  TemplateId: null,
  TemplateType: "Email",
  TemplateName: "",
  SubjectName: "",
  EmailTemplateText: "",
  SMSTemplateText: "",
};

const SMS_MAX_CHARS = 160;

function getTemplateBody(row) {
  const type = String(row?.TemplateType ?? "").trim().toLowerCase();
  if (type === "sms") {
    return String(row?.SMSTemplateText ?? row?.smsTemplateText ?? "").trim();
  }
  return String(
    row?.Templatetext ??
      row?.TemplateText ??
      row?.EmailTemplateText ??
      row?.emailTemplateText ??
      "",
  ).trim();
}

function getTemplateSubject(row) {
  return String(
    row?.EmailSubject ??
      row?.SubjectName ??
      row?.emailSubject ??
      row?.subjectName ??
      "",
  ).trim();
}

function isEmailBodyEmpty(html) {
  return !stripHtml(String(html || "")).trim();
}

function isTemplateArchived(row) {
  const status = String(row?.CampaignStatus ?? "").trim().toLowerCase();
  if (status === "archived") return true;
  const flag = String(row?.IsArchive ?? row?.isArchive ?? "").trim();
  return flag === "1" || flag.toLowerCase() === "true";
}

const CampaignTemplateList = () => {
  const { userData } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const { canViewCampaignTemplates, loading: accessLoading } =
    useParticipantAccess();
  const [rows, setRows] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState([]);
  const [archiveConfirm, setArchiveConfirm] = useState({
    open: false,
    row: null,
  });
  const [restoreConfirm, setRestoreConfirm] = useState({
    open: false,
    row: null,
  });
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const loadSeqRef = useRef(0);
  const orgId = String(userData?.OrganizationId || "").trim();

  const loadRows = useCallback(async () => {
    if (!orgId || !userData) return;
    const seq = ++loadSeqRef.current;
    setListLoading(true);
    try {
      const list = await fetchAllCampaignTemplates(userData);
      // Ignore stale responses from an earlier navigation/effect run.
      if (seq !== loadSeqRef.current) return;
      setRows(Array.isArray(list) ? list : []);
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      showAlert({
        message: err?.message || "Could not load campaign templates.",
      });
      setRows([]);
    } finally {
      if (seq === loadSeqRef.current) setListLoading(false);
    }
  }, [orgId, showAlert, userData]);

  useEffect(() => {
    if (accessLoading || !orgId) return undefined;
    loadRows();
    return () => {
      // Invalidate in-flight load when org/access changes or effect re-runs.
      loadSeqRef.current += 1;
    };
  }, [accessLoading, loadRows, orgId]);

  const visibleRows = useMemo(
    () =>
      (rows || []).filter((row) =>
        matchesCampaignStatusFilter(row, statusFilter),
      ),
    [rows, statusFilter],
  );

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setFormErrors({});
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setForm(EMPTY_FORM);
    setFormErrors({});
  };

  const openEdit = async (row) => {
    const templateId = getTemplateRowId(row);
    if (!templateId || !userData?.OrganizationId) return;

    if (isTemplateArchived(row)) {
      const type = String(row?.TemplateType ?? "").trim().toLowerCase();
      showAlert({
        message:
          type === "sms"
            ? "SMS template is archived."
            : "Email template is archived.",
      });
      return;
    }

    showLoader("Loading template...");
    try {
      const full = await fetchCampaignTemplateById(userData, templateId);
      const merged = full ? { ...row, ...full } : row;
      if (isTemplateArchived(merged)) {
        const type = String(merged?.TemplateType ?? "").trim().toLowerCase();
        showAlert({
          message:
            type === "sms"
              ? "SMS template is archived."
              : "Email template is archived.",
        });
        return;
      }

      const type = String(merged?.TemplateType ?? "Email").trim() || "Email";
      const isSms = type.toLowerCase() === "sms";
      setForm({
        TemplateId: templateId,
        TemplateType: isSms ? "SMS" : "Email",
        TemplateName: String(merged?.TemplateName ?? "").trim(),
        SubjectName: getTemplateSubject(merged),
        EmailTemplateText: isSms ? "" : getTemplateBody(merged),
        SMSTemplateText: isSms ? getTemplateBody(merged) : "",
      });
      setFormErrors({});
      setFormOpen(true);
    } catch (err) {
      showAlert({ message: err?.message || "Could not load campaign." });
    } finally {
      hideLoader();
    }
  };

  const requestArchive = (row) => {
    if (String(row?.CampaignStatus ?? "").trim() === "Archived") return;
    setArchiveConfirm({ open: true, row });
  };

  const requestRestore = (row) => {
    if (String(row?.CampaignStatus ?? "").trim() !== "Archived") return;
    setRestoreConfirm({ open: true, row });
  };

  const confirmArchive = async () => {
    const row = archiveConfirm.row;
    setArchiveConfirm({ open: false, row: null });
    const templateId = getTemplateRowId(row);
    if (!templateId) return;

    showLoader("Archiving template...");
    try {
      await archiveCampaignTemplate(userData, templateId);
      showAlert({ message: "Template archived.", type: "success" });
      await loadRows();
    } catch (err) {
      showAlert({ message: err?.message || "Could not archive template." });
    } finally {
      hideLoader();
    }
  };

  const confirmRestore = async () => {
    const row = restoreConfirm.row;
    setRestoreConfirm({ open: false, row: null });
    const templateId = getTemplateRowId(row);
    if (!templateId) return;

    showLoader("Restoring template...");
    try {
      await unarchiveCampaignTemplate(userData, templateId);
      showAlert({ message: "Template restored.", type: "success" });
      await loadRows();
    } catch (err) {
      showAlert({ message: err?.message || "Could not restore template." });
    } finally {
      hideLoader();
    }
  };

  const validateForm = (values) => {
    const errors = {};
    if (!String(values.TemplateName || "").trim()) {
      errors.TemplateName = "Template name is required.";
    }
    const isSms = String(values.TemplateType).toLowerCase() === "sms";
    if (!isSms && !String(values.SubjectName || "").trim()) {
      errors.SubjectName = "Subject is required.";
    }
    if (isSms) {
      const sms = String(values.SMSTemplateText || "");
      if (!sms.trim()) {
        errors.SMSTemplateText = "SMS message is required.";
      } else if (sms.length > SMS_MAX_CHARS) {
        errors.SMSTemplateText = `SMS message cannot exceed ${SMS_MAX_CHARS} characters.`;
      }
    } else if (isEmailBodyEmpty(values.EmailTemplateText)) {
      errors.EmailTemplateText = "Email body is required.";
    }
    return errors;
  };

  const handleFormSave = async (e) => {
    e.preventDefault();
    const errors = validateForm(form);
    setFormErrors(errors);
    if (Object.keys(errors).length) return;

    const isEdit = Boolean(form.TemplateId);
    showLoader(isEdit ? "Updating template..." : "Saving template...");
    try {
      const result = await createCampaignTemplate(userData, form);
      showAlert({
        message:
          result?.MSG ||
          (isEdit ? "Template updated." : "Template saved."),
        type: "success",
      });
      closeForm();
      await loadRows();
    } catch (err) {
      showAlert({ message: err?.message || "Could not save template." });
    } finally {
      hideLoader();
    }
  };

  const renderCell = (row, field) => {
    if (field === "TemplateName") {
      const name = String(row?.TemplateName ?? "").trim() || "—";
      if (name === "—") return name;
      return (
        <button
          type="button"
          className="report-table__cell-link pm-campaign-name-link"
          onClick={() => openEdit(row)}
          title="Edit campaign"
        >
          {name}
        </button>
      );
    }

    if (field === "CampaignStatus") {
      const status = String(row?.CampaignStatus ?? "").trim() || "—";
      if (status === "—") return status;
      const isArchived = status === "Archived";
      return (
        <span className="pm-campaign-status-cell">
          <span
            className="pm-status-pill pm-status-pill--readonly pm-status-pill--square"
            style={getCampaignStatusPillStyle(status)}
          >
            {status}
          </span>
          {isArchived ? (
            <button
              type="button"
              className="pm-campaign-icon-btn"
              title="Restore template"
              aria-label="Restore template"
              onClick={() => requestRestore(row)}
            >
              <i className="fas fa-undo" aria-hidden="true" />
            </button>
          ) : (
            <button
              type="button"
              className="pm-campaign-icon-btn pm-campaign-icon-btn--danger"
              title="Archive template"
              aria-label="Archive template"
              onClick={() => requestArchive(row)}
            >
              <i className="fas fa-trash-alt" aria-hidden="true" />
            </button>
          )}
        </span>
      );
    }

    return String(row?.[field] ?? "—");
  };

  const renderColumnSearch = useCallback(
    (field, api) => {
      if (api?.checkOnly) {
        return field === "CampaignStatus";
      }
      if (field === "CampaignStatus") {
        return (
          <BadgeMultiSelect
            options={CAMPAIGN_STATUS_FILTER_OPTIONS}
            value={statusFilter}
            onChange={setStatusFilter}
            emptyLabel="All status"
            addLabel="+"
          />
        );
      }
      return null;
    },
    [statusFilter],
  );

  const matchColumnSearch = useCallback((row, field, query) => {
    if (field === "CampaignStatus") return true;
    return matchTextColumnSearch(row, field, query);
  }, []);

  if (accessLoading) {
    return <div className="participant-manager-page pm-page--bootstrapping" />;
  }
  if (!canViewCampaignTemplates) {
    return <Navigate to="/notification-center" replace />;
  }

  const archiveName =
    String(archiveConfirm.row?.TemplateName ?? "").trim() || "this template";
  const restoreName =
    String(restoreConfirm.row?.TemplateName ?? "").trim() || "this template";
  const isEditMode = Boolean(form.TemplateId);
  const isSmsForm = String(form.TemplateType).toLowerCase() === "sms";

  return (
    <div className="participant-manager-page pm-list-page">
      <PageBreadcrumb
        items={[
          { label: "Home", path: "/notification-center" },
          { label: "Campaign Templates" },
        ]}
        actions={[
          {
            icon: "fas fa-refresh",
            label: "Refresh",
            onClick: loadRows,
          },
          {
            icon: "fas fa-plus",
            label: "Create Template",
            onClick: openCreate,
          },
        ]}
      />

      <div className="pm-report-wrap">
        <ReportTable
          rows={visibleRows}
          fieldOptions={CAMPAIGN_TEMPLATE_FIELD_OPTIONS}
          defaultColumns={DEFAULT_CAMPAIGN_TEMPLATE_COLUMNS}
          hideColumnHeaderActionsFields={["CampaignStatus"]}
          renderCell={renderCell}
          renderColumnSearch={renderColumnSearch}
          matchColumnSearch={matchColumnSearch}
          defaultPageSize="all"
          persistLayoutKey="pm-campaign-templates-v1"
          emptyMessage="No campaign templates found."
          loading={listLoading}
        />
      </div>

      <CtmsWorkspaceModal
        open={formOpen}
        title={isEditMode ? "Update Template" : "Create Template"}
        titleId="pm-campaign-form-title"
        onClose={closeForm}
        className="ctms-workspace-modal--compact-host"
        dialogClassName="ctms-workspace-modal__dialog--campaign"
        contentClassName="pm-campaign-modal"
      >
        <form
          className="pm-campaign-form"
          onSubmit={handleFormSave}
          noValidate
        >
          <div className="pm-campaign-form__scroll">
            <div className="pm-campaign-form__stack">
              <div className="pm-campaign-form__field">
                <label className="pm-campaign-form__label">
                  Type <span className="pm-add-field__req">*</span>
                </label>
                <div className="pm-campaign-type-toggle">
                  {["Email", "SMS"].map((type) => (
                    <button
                      key={type}
                      type="button"
                      className={`pm-campaign-type-toggle__btn${
                        form.TemplateType === type ? " is-active" : ""
                      }`}
                      disabled={isEditMode}
                      onClick={() => {
                        if (isEditMode) return;
                        setForm((prev) => ({
                          ...prev,
                          TemplateType: type,
                          SubjectName:
                            type === "SMS" ? "" : prev.SubjectName,
                          EmailTemplateText:
                            type === "SMS" ? "" : prev.EmailTemplateText,
                          SMSTemplateText:
                            type === "Email" ? "" : prev.SMSTemplateText,
                        }));
                        setFormErrors((prev) => {
                          const next = { ...prev };
                          delete next.SubjectName;
                          delete next.EmailTemplateText;
                          delete next.SMSTemplateText;
                          return next;
                        });
                      }}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>
              <div className="pm-campaign-form__field">
                <label className="pm-campaign-form__label">
                  Template name <span className="pm-add-field__req">*</span>
                </label>
                <input
                  className={`form-control${
                    formErrors.TemplateName ? " is-invalid" : ""
                  }`}
                  value={form.TemplateName}
                  onChange={(e) => {
                    setForm((prev) => ({
                      ...prev,
                      TemplateName: e.target.value,
                    }));
                    setFormErrors((prev) => {
                      if (!prev.TemplateName) return prev;
                      const next = { ...prev };
                      delete next.TemplateName;
                      return next;
                    });
                  }}
                  placeholder="Enter template name"
                />
                {formErrors.TemplateName ? (
                  <span className="pm-add-field__error" role="alert">
                    {formErrors.TemplateName}
                  </span>
                ) : null}
              </div>
              {!isSmsForm ? (
                <div className="pm-campaign-form__field">
                  <label className="pm-campaign-form__label">
                    Subject <span className="pm-add-field__req">*</span>
                  </label>
                  <input
                    className={`form-control${
                      formErrors.SubjectName ? " is-invalid" : ""
                    }`}
                    value={form.SubjectName}
                    onChange={(e) => {
                      setForm((prev) => ({
                        ...prev,
                        SubjectName: e.target.value,
                      }));
                      setFormErrors((prev) => {
                        if (!prev.SubjectName) return prev;
                        const next = { ...prev };
                        delete next.SubjectName;
                        return next;
                      });
                    }}
                    placeholder="Email subject"
                  />
                  {formErrors.SubjectName ? (
                    <span className="pm-add-field__error" role="alert">
                      {formErrors.SubjectName}
                    </span>
                  ) : null}
                </div>
              ) : null}
              <div
                className={`pm-campaign-form__field${
                  isSmsForm ? "" : " pm-campaign-form__field--editor"
                }`}
              >
                <div className="pm-campaign-form__label-row">
                  <label className="pm-campaign-form__label">
                    {isSmsForm ? "SMS message" : "Email body"}{" "}
                    <span className="pm-add-field__req">*</span>
                  </label>
                  {isSmsForm ? (
                    <span
                      className={`pm-campaign-char-count${
                        form.SMSTemplateText.length >= SMS_MAX_CHARS
                          ? " is-max"
                          : ""
                      }`}
                    >
                      {form.SMSTemplateText.length}/{SMS_MAX_CHARS}
                    </span>
                  ) : null}
                </div>
                {isSmsForm ? (
                  <textarea
                    className={`form-control${
                      formErrors.SMSTemplateText ? " is-invalid" : ""
                    }`}
                    rows={8}
                    maxLength={SMS_MAX_CHARS}
                    value={form.SMSTemplateText}
                    onChange={(e) => {
                      const nextValue = e.target.value.slice(0, SMS_MAX_CHARS);
                      setForm((prev) => ({
                        ...prev,
                        SMSTemplateText: nextValue,
                      }));
                      setFormErrors((prev) => {
                        if (!prev.SMSTemplateText) return prev;
                        const next = { ...prev };
                        delete next.SMSTemplateText;
                        return next;
                      });
                    }}
                    placeholder={`Enter SMS message (max ${SMS_MAX_CHARS} characters)`}
                  />
                ) : (
                  <div
                    className={`pm-campaign-editor${
                      formErrors.EmailTemplateText ? " is-invalid" : ""
                    }`}
                  >
                    <TextEditor
                      key={form.TemplateId || "create-email"}
                      value={form.EmailTemplateText}
                      onChange={(html) => {
                        setForm((prev) => ({
                          ...prev,
                          EmailTemplateText: html,
                        }));
                        setFormErrors((prev) => {
                          if (!prev.EmailTemplateText) return prev;
                          const next = { ...prev };
                          delete next.EmailTemplateText;
                          return next;
                        });
                      }}
                      placeholder="Enter email body"
                      height="22rem"
                      minHeight="16rem"
                      showImageButton={false}
                      blockImages
                      showCodeBlock={false}
                      error={Boolean(formErrors.EmailTemplateText)}
                      className="pm-campaign-quill"
                    />
                  </div>
                )}
                {formErrors.SMSTemplateText || formErrors.EmailTemplateText ? (
                  <span className="pm-add-field__error" role="alert">
                    {formErrors.SMSTemplateText || formErrors.EmailTemplateText}
                  </span>
                ) : isSmsForm ? (
                  <span className="pm-campaign-form__hint">
                    SMS messages are limited to {SMS_MAX_CHARS} characters.
                  </span>
                ) : null}
              </div>
            </div>
          </div>
          <div className="pm-campaign-form__footer">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={closeForm}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              <i
                className={`fas ${isEditMode ? "fa-redo" : "fa-save"} me-1`}
                aria-hidden="true"
              />
              {isEditMode ? "Update Template" : "Save Template"}
            </button>
          </div>
        </form>
      </CtmsWorkspaceModal>

      <ConfirmModal
        isOpen={archiveConfirm.open}
        onClose={() => setArchiveConfirm({ open: false, row: null })}
        onConfirm={confirmArchive}
        title="Archive template"
        message={`Are you sure you want to archive "${archiveName}"? You can restore it later with Undo.`}
        confirmText="Archive"
        cancelText="Cancel"
        confirmButtonClass="btn-danger"
        icon="fas fa-trash-alt text-danger"
      />

      <ConfirmModal
        isOpen={restoreConfirm.open}
        onClose={() => setRestoreConfirm({ open: false, row: null })}
        onConfirm={confirmRestore}
        title="Restore template"
        message={`Restore "${restoreName}" to Active?`}
        confirmText="Restore"
        cancelText="Cancel"
        confirmButtonClass="btn-primary"
        icon="fas fa-undo text-primary"
      />

      <InfoAlert {...alertState} onClose={hideAlert} />
    </div>
  );
};

export default CampaignTemplateList;
